/**
 * GWP and Free Samples Monitors
 * --------------------------------------------------
 * This script manages the Gift With Purchase (GWP) and Free Samples logic in the Shopify cart drawer.
 *
 * Features:
 * - Automatically adds/removes GWP gifts and free samples based on cart value, promo rules, and UTM/customer tag restrictions.
 * - Supports multiple simultaneous promotions, preview/production modes, and user/tag/UTM restrictions.
 * - Integrates with backend Metaobject Schema and Shopify Functions for price adjustments and promo validation.
 * - Ensures promo items are only present when the customer qualifies, and removes them if conditions are not met.
 *
 * Usage:
 * - GWP promo data is injected into the page by Liquid and accessed via window.getFreeGiftPromos().
 * - Free sample thresholds are set in the DOM using data attributes.
 * - The monitor is automatically initialized on page load and listens for cart changes.
 *
 * For full details on backend functionality check on https://github.com/Kenvue-NA-Emerging-Channels-Technology/GWP-Cart-Transform/edit/main/README.md.
 */

// GwpAndFreeSamplesMonitor
// Monitors cart changes to manage GWP (Gift With Purchase) and free samples logic in the Shopify cart drawer.
// Handles automatic addition/removal of free gifts and samples based on cart state, UTM, and promo rules.

class GwpAndFreeSamplesMonitor {
  // Suppress cart_changed event handling during monitor-triggered cart changes
  suppressCartChanged = false;
  /**
   * Initializes monitoring state and section selectors, and sets up event listeners.
   */
  constructor() {
    this.isMonitoring = false;
    this.sectionsToRender = [
      {
        section_id: 'cart-drawer',
        selectors: ['.drawer__header', 'cart-drawer-items', '.drawer__footer'],
      },
      {
        section_id: 'cart-icon-bubble',
        selectors: ['.cart-count-bubble'],
      },
    ];
    this.init();
  }

  /**
   * Sets up event listeners for cart changes and DOMContentLoaded.
   */
  init() {
    window.addEventListener('cart_changed', async (e) => {
      // Prevent infinite loop: ignore cart_changed events triggered by monitor actions
      if (this.suppressCartChanged) return;
      // On cart change, check for real changes and monitor free items threshold
      const addedChanges = e?.detail?.changes?.added?.filter(
        (item) => !item.properties.hasOwnProperty('_strikethrough') || item.properties.hasOwnProperty('_gwp_promo')
      );
      const removedChanges = e?.detail?.changes?.removed?.filter(
        (item) => !item.properties.hasOwnProperty('_strikethrough') || item.properties.hasOwnProperty('_gwp_promo')
      );
      const realChanges = [...addedChanges, ...removedChanges];
      if (!realChanges || realChanges.length == 0) return;
      await this.monitorFreeItemsThreshold();
    });
    document.addEventListener('DOMContentLoaded', async () => {
      // On page load, remove unavailable free items from cart
      await this.monitorUnavailableItems();
    });
  }

  /**
   * Updates the live cart content in the DOM with new HTML from the server.
   * @param {Object} updatedSections - Sections HTML keyed by section_id.
   */
  async updateLiveContent(updatedSections) {
    try {
      for (const section of this.sectionsToRender) {
        const content = updatedSections[section.section_id];
        const html = new DOMParser().parseFromString(content, 'text/html');
        section.selectors.forEach((selector) => {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        });
      }
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Fetches the current cart state from the Shopify API.
   * @returns {Promise<Object>} The cart object.
   */
  async fetchCart() {
    try {
      const response = await fetch(routes.cart_url, fetchConfig());
      const data = await response.json();
      return data;
    } catch (e) {
      console.error(e);
      throw new Error('Error fetching cart');
    }
  }

  /**
   * Adds free gift variants to the cart in bulk.
   * @param {Array} variants - Array of variant IDs.
   * @param {Object} properties - Additional properties for the cart items.
   */
  async bulkAddFreeGift(variants, properties = {}) {
    try {
      this.suppressCartChanged = true;
      const body = JSON.stringify({
        items: variants.map((variant_id) => ({
          id: variant_id,
          quantity: 1,
          properties: { _free_item_type: 'Free Gift', ...properties },
        })),
        sections: this.sectionsToRender.map((section) => section.section_id),
      });
      const response = await fetch(routes.cart_add_url, { ...fetchConfig(), ...{ body } });
      const data = await response.json();
      await this.updateLiveContent(data.sections);
    } catch (e) {
      console.error(e);
    } finally {
      this.suppressCartChanged = false;
    }
  }

  /**
   * Removes variants from the cart in bulk.
   * @param {Array} variants - Array of variant IDs to remove.
   */
  async bulkRemoveFromCart(variants) {
    try {
      this.suppressCartChanged = true;
      const updates = {};
      variants.forEach((variantId) => {
        updates[variantId] = 0;
      });
      const body = JSON.stringify({
        updates,
        sections: this.sectionsToRender.map((section) => section.section_id),
      });
      const response = await fetch(routes.cart_update_url, { ...fetchConfig(), ...{ body } });
      const data = await response.json();
      await this.updateLiveContent(data.sections);
    } catch (e) {
      console.error(e);
    } finally {
      this.suppressCartChanged = false;
    }
  }

  /**
   * Filters out strikethrough and GWP promo items from the cart.
   * @param {Array} items - Cart items array.
   * @returns {Array} Filtered cart items.
   */
  _getRealCartItems(items) {
    return (
      items?.filter(
        (item) => !item.properties.hasOwnProperty('_strikethrough') && !item.properties.hasOwnProperty('_gwp_promo')
      ) ?? []
    );
  }

  /**
   * Gets the free samples threshold from the DOM, or returns null if not found.
   * @returns {number|null}
   */
  _getFreeSamplesThreshold() {
    const thresholdTag = document.querySelector('[data-free-samples-threshold]');
    if (!thresholdTag) return null;
    return parseInt(thresholdTag.dataset.freeSamplesThreshold);
  }

  /**
   * Gets the list of free samples in the cart.
   * @param {Array} items - Cart items array.
   * @returns {Array}
   */
  _getFreeSamples(items) {
    return items.filter((item) => item.properties._free_item_type === 'Free Samples');
  }

  /**
   * Gets the list of free gift variants in the cart for a given promo.
   * @param {Array} items - Cart items array.
   * @param {string} promoHandle - Promo handle to match.
   * @returns {Array}
   */
  _getGwpItemsInCart(items, promoHandle) {
    return items.filter((item) => item.properties['_gwp_promo'] == promoHandle);
  }

  /**
   * Gets the list of non-GWP items in the cart.
   * @param {Array} items - Cart items array.
   * @returns {Array}
   */
  _getOtherItemsInCart(items) {
    return items.filter((item) => !item.properties['_gwp_promo']);
  }

  /**
   * Handles the Free Samples logic: removes free samples if cart total is below threshold.
   * @param {Object} filteredCart - Cart object with filtered items and total_price.
   */
  async _handleFreeSamples(filteredCart) {
    // 1. Get the free samples threshold from the DOM (set by Liquid in the cart drawer)
    const freeSamplesWidgetThreshold = this._getFreeSamplesThreshold();
    if (freeSamplesWidgetThreshold !== null) {
      // 2. Get all free sample items currently in the cart
      const freeSamples = this._getFreeSamples(filteredCart.items);
      // 3. If there are free samples in the cart, but the cart total is now below the threshold,
      //    remove those free samples (user removed items or applied a discount)
      if (freeSamples.length && filteredCart.total_price < freeSamplesWidgetThreshold) {
        const freeSamplesVariants = freeSamples.map((item) => item.variant_id);
        await this.bulkRemoveFromCart(freeSamplesVariants);
      }
    }
  }

  /**
   * Handles the GWP (Gift With Purchase) logic: adds or removes GWP gifts based on promo rules.
   * @param {Object} cartRaw - Raw cart object from Shopify API.
   * @param {Object} filteredCart - Cart object with filtered items and total_price.
   */
  async _handleGwpPromos(cartRaw, filteredCart) {
    // 1. Get all active GWP promos from the global window (populated by Liquid in cart-drawer.liquid)
    const gwpPromos = window.getFreeGiftPromos ? window.getFreeGiftPromos() : null;
    if (!gwpPromos) return;
    for (const gwpPromo of gwpPromos) {
      // 2. Determine if this promo should auto-add gifts (based on UTM or always-on)
      let gwpAutoAdd = gwpPromo?.automatically_add_to_cart ?? false;
      if (gwpPromo?.utm_target && gwpPromo.automatically_add_to_cart) {
        // If promo is UTM-targeted, only auto-add if UTM matches or is stored in localStorage
        gwpAutoAdd = false;
        const urlParams = new URLSearchParams(window.location.search);
        const storedUtmTarget = localStorage.getItem('gwp_utm_target')
          ? JSON.parse(localStorage.getItem('gwp_utm_target'))
          : null;
        const currentUtmTarget = urlParams.get('utm_content');
        if (
          currentUtmTarget?.toLowerCase() === gwpPromo.utm_target?.toLowerCase() ||
          storedUtmTarget?.find((t) => t.toLowerCase() === gwpPromo.utm_target?.toLowerCase())
        ) {
          gwpAutoAdd = true;
        }
      }
      // 3. Get the threshold for this promo and which GWP variants are in the cart
      const freeGiftThreshold = gwpPromo?.cart_threshold ?? null;
      // Get all GWP (Gift With Purchase) items in the cart for this promo
      const gwpItemsInCart = this._getGwpItemsInCart(cartRaw.items, gwpPromo.promo_handle);
      const gwpItemVariantIdsInCart = gwpItemsInCart.map((item) => item.variant_id);
      const gwpLineKeysInCart = gwpItemsInCart.map((item) => item.key);
      // 4. Get all non-GWP items in the cart (to check if cart is empty except for gifts)
      const otherItemsInCart = this._getOtherItemsInCart(filteredCart.items);
      // 5. If cart is empty (except for gifts), or below threshold, remove GWP gifts
      if (
        !otherItemsInCart ||
        (otherItemsInCart.length == 0 && gwpLineKeysInCart.length > 0) ||
        (freeGiftThreshold != null && filteredCart.total_price < freeGiftThreshold)
      ) {
        await this.bulkRemoveFromCart(gwpLineKeysInCart);
      } else if (
        // 6. If promo is active, threshold is met, and gifts are not already in cart, add them
        gwpAutoAdd &&
        freeGiftThreshold != null &&
        filteredCart.total_price >= freeGiftThreshold &&
        filteredCart.items.length > 0
      ) {
        const freeGiftVariants = gwpPromo?.gwp_products?.map((variant) => variant.id);
        // Only add variants that are not already in the cart
        const variantsToAdd = freeGiftVariants?.filter((variant) => !gwpItemVariantIdsInCart.includes(variant));
        if (!variantsToAdd || variantsToAdd.length == 0) continue;
        const promoHandle = gwpPromo.promo_handle;
        await this.bulkAddFreeGift(variantsToAdd, { _gwp_promo: promoHandle });
      }
    }
  }

  /**
   * Monitors the cart for free samples and GWP logic, adding or removing items as needed.
   */
  async monitorFreeItemsThreshold() {
    try {
      if (this.isMonitoring) return;
      this.isMonitoring = true;
      const cartRaw = await this.fetchCart();
      if (!cartRaw) {
        console.error('Cart not found');
        return;
      }
      // Filter out strikethrough and GWP promo items
      const realCartItems = this._getRealCartItems(cartRaw.items);
      const realTotalPrice = realCartItems.reduce((acc, item) => acc + item.final_line_price, 0);
      const filteredCart = { ...cartRaw, items: realCartItems, total_price: realTotalPrice };
      if (!filteredCart) return;
      // Handle Free Samples logic
      await this._handleFreeSamples(filteredCart);
      // Handle GWP logic
      await this._handleGwpPromos(cartRaw, filteredCart);
    } catch (e) {
      console.error(e);
    } finally {
      this.isMonitoring = false;
    }
  }

  /**
   * Removes unavailable free items from the cart on page load.
   * @returns {Promise<boolean>} True if items were removed, false otherwise.
   */
  async monitorUnavailableItems() {
    try {
      if (this.isMonitoring) return;
      const dataElement = document.querySelector('#freeItemsToRemove');
      if (!dataElement) return false;
      const variants_to_remove = JSON.parse(dataElement.textContent);
      if (!variants_to_remove || variants_to_remove.length == 0) return false;
      this.isMonitoring = true;
      await this.bulkRemoveFromCart(variants_to_remove);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    } finally {
      this.isMonitoring = false;
    }
  }
}

// Initialize the monitor on page load
new GwpAndFreeSamplesMonitor();